import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import '../styles/AdminLayout.css';

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [openMenuTitle, setOpenMenuTitle] = useState(null);
  const { adminUser, logout, isSuperAdmin } = useAdminAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { title: 'Dashboard', path: '/admin/dashboard', icon: '📊' },
    { title: 'Admin Users', path: '/admin/users', icon: '👥', superAdminOnly: true },
    { 
      title: 'Properties', 
      icon: '🏠',
      submenu: [
        { title: 'Properties', path: '/admin/properties' },
        { title: 'Property Types', path: '/admin/properties-type' },
        { title: 'Tags', path: '/admin/tags' },
        { title: 'Sections', path: '/admin/sections' },
      ]
    },
    { 
      title: 'Investors', 
      icon: '👤',
      submenu: [
        { title: 'Investors', path: '/admin/investor' },
      ]
    },
    { 
      title: 'Transactions', 
      icon: '💰',
      submenu: [
        { title: 'Purchase Transactions', path: '/admin/purchase-transactions' },
        { title: 'Top-up Transactions', path: '/admin/topup-transaction' },
        { title: 'Withdrawal Transactions', path: '/admin/withdrawal-transaction' },
        { title: 'For Sale', path: '/admin/for-sale' },
        { title: 'Cash Wallet', path: '/admin/cash-wallet' },
      ]
    },
    { 
      title: 'Content', 
      icon: '📝',
      submenu: [
        { title: 'Blogs', path: '/admin/blog' },
        { title: 'Blog Categories', path: '/admin/blog-category' },
        { title: 'Questions', path: '/admin/question' },
        { title: 'Answer Investor', path: '/admin/answer-investor' },
        { title: 'Contact Us', path: '/admin/contact-us' },
      ]
    },
    { 
      title: 'System', 
      icon: '⚙️',
      submenu: [
        { title: 'Countries', path: '/admin/country' },
        { title: 'Currencies', path: '/admin/currency' },
      ]
    },
  ];

  const MenuItem = ({ item }) => {
    const isOpen = openMenuTitle === item.title;

    if (item.superAdminOnly && !isSuperAdmin()) {
      return null;
    }

    const handleMenuClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isOpen) {
        setOpenMenuTitle(null);
      } else {
        setOpenMenuTitle(item.title);
      }
    };

    if (item.submenu) {
      return (
        <div className={`admin-menu-item has-submenu ${isOpen ? 'open' : ''}`}>
          <button 
            type="button"
            className="admin-menu-link" 
            onClick={handleMenuClick}
            style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left' }}
          >
            <span className="admin-menu-icon">{item.icon}</span>
            {sidebarOpen && (
              <>
                <span className="admin-menu-text">{item.title}</span>
                <span className="admin-menu-arrow">{isOpen ? '▼' : '▶'}</span>
              </>
            )}
          </button>
          <div className="admin-submenu">
            {item.submenu.map((subItem, index) => (
              <NavLink 
                key={index} 
                to={subItem.path} 
                className={({ isActive }) => `admin-submenu-link ${isActive ? 'active' : ''}`}
              >
                {subItem.title}
              </NavLink>
            ))}
          </div>
        </div>
      );
    }

    return (
      <NavLink 
        to={item.path} 
        className={({ isActive }) => `admin-menu-item admin-menu-link ${isActive ? 'active' : ''}`}
      >
        <span className="admin-menu-icon">{item.icon}</span>
        {sidebarOpen && <span className="admin-menu-text">{item.title}</span>}
      </NavLink>
    );
  };

  return (
    <div className={`admin-layout ${sidebarOpen ? '' : 'sidebar-collapsed'}`}>
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <h2>{sidebarOpen ? 'Tannit Admin' : 'TA'}</h2>
          <button 
            className="admin-sidebar-toggle" 
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>
        </div>
        
        <nav className="admin-nav">
          {menuItems.map((item, index) => (
            <MenuItem key={index} item={item} />
          ))}
        </nav>
        
        <div className="admin-sidebar-footer">
          <div className="admin-user-info">
            {sidebarOpen && (
              <>
                <span className="admin-user-name">{adminUser?.name}</span>
                <span className="admin-user-role">{adminUser?.role === 'super_admin' ? 'Super Admin' : 'Admin'}</span>
              </>
            )}
          </div>
          <button className="admin-logout-btn" onClick={handleLogout}>
            {sidebarOpen ? 'Logout' : '🚪'}
          </button>
        </div>
      </aside>
      
      <main className="admin-main">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
